import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { TOOLTIPS, type TooltipId } from "../lib/tooltips";

/** Space kept between the "?" and its popup, and between the popup and the window's edge. */
const GAP = 6;
const EDGE = 8;

/**
 * Whether the focus now arriving came from Tab. A hint opens on keyboard
 * focus, but not on focus a script moves (a dialog opening) or a pointer
 * lands (a press on the "?", which the click handles) - #420. The last
 * Tab keydown or pointerdown decides; nothing else touches it.
 */
let tabbing = false;
if (typeof document !== "undefined") {
  document.addEventListener("keydown", (e) => (tabbing = e.key === "Tab"), true);
  document.addEventListener("pointerdown", () => (tabbing = false), true);
  document.addEventListener("mousedown", () => (tabbing = false), true);
}

/**
 * A small "?" beside a term that shows its line from the tooltip register
 * on hover, on a click or tap, and on keyboard focus - only when asked
 * (#420): a dialog that opens with focus near it does not open it. The text is also the control's accessible
 * description, so a screen reader hears it without the pointer.
 *
 * The popup is fixed to the window, not placed inside the page (#401): an
 * absolutely placed popup lay over the control the "?" labels and, inside
 * a dialog, over its buttons, and made the dialog scroll to fit it. It
 * opens above the "?" - what a label describes sits below or beside it -
 * and below only where the window has no room above.
 */
export function Hint({ id, label }: { id: TooltipId; label?: string }) {
  const text = TOOLTIPS[id];
  const descId = useId();
  // Each way of asking is held apart, so leaving one does not undo another: a tap survives the pointer moving off.
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [keyed, setKeyed] = useState(false);
  const open = hovered || pinned || keyed;
  const close = () => {
    setHovered(false);
    setPinned(false);
    setKeyed(false);
  };
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLSpanElement>(null);

  const place = useCallback(() => {
    const b = button.current?.getBoundingClientRect();
    const p = popup.current?.getBoundingClientRect();
    if (!b || !p) return;
    const above = b.top - GAP - p.height;
    const top = above >= EDGE ? above : b.bottom + GAP;
    const left = Math.min(Math.max(b.left + b.width / 2 - p.width / 2, EDGE), window.innerWidth - p.width - EDGE);
    setAt({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setAt(null);
      return;
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  return (
    <span className="relative inline-block align-middle">
      <button
        ref={button}
        data-hint=""
        type="button"
        aria-label={label ? `About ${label}` : "More about this"}
        aria-describedby={descId}
        aria-expanded={open}
        // A click opens it, and a second click closes it - hover had already opened it before the first.
        onClick={() => (pinned ? close() : setPinned(true))}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => tabbing && setKeyed(true)}
        onBlur={() => {
          setPinned(false);
          setKeyed(false);
        }}
        onKeyDown={(e) => {
          if (e.key !== "Escape" || !open) return;
          // The first Escape closes the hint alone, not the dialog around it.
          e.preventDefault();
          e.stopPropagation();
          close();
        }}
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-neutral-400 text-[10px] font-semibold leading-none text-neutral-500 hover:border-ink hover:text-ink dark:border-neutral-600 dark:text-neutral-400 dark:hover:text-white"
      >
        ?
      </button>
      <span
        ref={popup}
        id={descId}
        role="tooltip"
        // Coordinates are measured, so they cannot be classes; everything else is.
        style={open && at ? { top: at.top, left: at.left } : undefined}
        // Closed, it is only the "?"'s description: sr-only and nothing else, or its width still widens the page (#401).
        className={open ? `fixed z-50 ${at ? "" : "invisible left-0 top-0"} pointer-events-none w-64 max-w-[calc(100vw-1rem)] rounded-lg border border-neutral-200 bg-white p-2.5 text-left text-xs font-normal normal-case tracking-normal text-neutral-800 shadow-lg dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100` : "sr-only"}
      >
        {text}
      </span>
    </span>
  );
}
